/**
 * File: GenericToolUI.tsx
 * Description: Generic tool UI component for all tools used by the chat agent
 * Author: AI Assistant
 * Created: 2025-09-10
 * Last Modified: 2025-09-10
 */

import { makeAssistantToolUI } from '@assistant-ui/react';
import { type FC, useState } from 'react';

import { MarkdownText } from './MarkdownText';

// Generic types for all tools
type GenericToolArgs = {
  query?: string;
  input?: string; // Fallback if parsing fails
  [key: string]: any; // Allow any additional properties
};

type GenericToolResult = {
  [key: string]: any; // Allow any result structure
};

interface GenericToolUIProps {
  args: GenericToolArgs;
  status: any;
  result?: any;
  toolName: string;
}

type ColorName = 'blue' | 'green' | 'purple' | 'orange' | 'cyan' | 'indigo' | 'gray' | 'amber' | 'teal' | 'violet' | 'pink' | 'sky';

type ColorClasses = {
  border: string;
  bg: string;
  text: string;
  textSecondary: string;
  spinner: string;
  button: string;
  cardBorder: string;
  cardText: string;
};

type ToolConfig = {
  displayName: string;
  color: ColorName;
  emoji: string;
  description: string;
};

// Mapping for tool-specific styling and configuration
const getToolConfig = (toolName: string): ToolConfig => {
  switch (toolName) {
    case 'search_wiki':
      return {
        displayName: 'Wiki Search',
        color: 'purple' as const,
        emoji: '📚',
        description: 'Searching organizational knowledge base...',
      };
    case 'search_guidelines':
      return {
        displayName: 'Guidelines Search',
        color: 'blue' as const,
        emoji: '📋',
        description: 'Searching organizational guidelines...',
      };
    case 'search_user_docs':
      return {
        displayName: 'User Documents Search',
        color: 'green' as const,
        emoji: '📄',
        description: 'Searching project documents...',
      };
    case 'search_brownfield_docs':
      return {
        displayName: 'Brownfield Search',
        color: 'amber' as const,
        emoji: '🏗️',
        description: 'Searching existing architecture documents...',
      };
    case 'lookup_application_ids':
      return {
        displayName: 'Application Lookup',
        color: 'teal' as const,
        emoji: '🔍',
        description: 'Looking up application IDs...',
      };
    case 'read_project_adrs':
      return {
        displayName: 'Project ADRs',
        color: 'violet' as const,
        emoji: '📚',
        description: 'Reading architecture decision records...',
      };
    case 'read_project_instructions':
      return {
        displayName: 'Project Instructions',
        color: 'pink' as const,
        emoji: '📝',
        description: 'Reading project instructions...',
      };
    case 'fetch_webpage':
      return {
        displayName: 'Web Resource',
        color: 'sky' as const,
        emoji: '🌐',
        description: 'Fetching external documentation...',
      };
    case 'read_business_requirements':
      return {
        displayName: 'Business Requirements',
        color: 'orange' as const,
        emoji: '📋',
        description: 'Reading business requirements...',
      };
    case 'read_generated_content':
      return {
        displayName: 'Generated Content',
        color: 'cyan' as const,
        emoji: '🤖',
        description: 'Reading generated sections...',
      };
    case 'read_project_context':
      return {
        displayName: 'Project Context',
        color: 'indigo' as const,
        emoji: '📁',
        description: 'Reading project context...',
      };
    default: {
      // Fallback for any tool that doesn't have a specific configuration yet
      // This makes the tool name more readable by converting snake_case to Title Case
      const formattedName = toolName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return {
        displayName: formattedName,
        color: 'gray' as const,
        emoji: '🔧',
        description: `Executing ${formattedName.toLowerCase()}...`,
      };
    }
  }
};

// Color classes mapping
const colorClasses: Record<ColorName, ColorClasses> = {
  blue: {
    border: 'border-blue-200 dark:border-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/70',
    text: 'text-blue-800 dark:text-blue-100',
    textSecondary: 'text-blue-700 dark:text-blue-200',
    spinner: 'border-blue-500',
    button: 'text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100',
    cardBorder: 'border-blue-100 dark:border-blue-600',
    cardText: 'text-blue-900 dark:text-blue-50',
  },
  green: {
    border: 'border-green-200 dark:border-green-600',
    bg: 'bg-green-50 dark:bg-green-900/70',
    text: 'text-green-800 dark:text-green-100',
    textSecondary: 'text-green-700 dark:text-green-200',
    spinner: 'border-green-500',
    button: 'text-green-600 hover:text-green-800 dark:text-green-300 dark:hover:text-green-100',
    cardBorder: 'border-green-100 dark:border-green-600',
    cardText: 'text-green-900 dark:text-green-50',
  },
  purple: {
    border: 'border-purple-200 dark:border-purple-600',
    bg: 'bg-purple-50 dark:bg-purple-900/70',
    text: 'text-purple-800 dark:text-purple-100',
    textSecondary: 'text-purple-700 dark:text-purple-200',
    spinner: 'border-purple-500',
    button: 'text-purple-600 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-100',
    cardBorder: 'border-purple-100 dark:border-purple-600',
    cardText: 'text-purple-900 dark:text-purple-50',
  },
  orange: {
    border: 'border-orange-200 dark:border-orange-600',
    bg: 'bg-orange-50 dark:bg-orange-900/70',
    text: 'text-orange-800 dark:text-orange-100',
    textSecondary: 'text-orange-700 dark:text-orange-200',
    spinner: 'border-orange-500',
    button: 'text-orange-600 hover:text-orange-800 dark:text-orange-300 dark:hover:text-orange-100',
    cardBorder: 'border-orange-100 dark:border-orange-600',
    cardText: 'text-orange-900 dark:text-orange-50',
  },
  cyan: {
    border: 'border-cyan-200 dark:border-cyan-600',
    bg: 'bg-cyan-50 dark:bg-cyan-900/70',
    text: 'text-cyan-800 dark:text-cyan-100',
    textSecondary: 'text-cyan-700 dark:text-cyan-200',
    spinner: 'border-cyan-500',
    button: 'text-cyan-600 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-cyan-100',
    cardBorder: 'border-cyan-100 dark:border-cyan-600',
    cardText: 'text-cyan-900 dark:text-cyan-50',
  },
  indigo: {
    border: 'border-indigo-200 dark:border-indigo-600',
    bg: 'bg-indigo-50 dark:bg-indigo-900/70',
    text: 'text-indigo-800 dark:text-indigo-100',
    textSecondary: 'text-indigo-700 dark:text-indigo-200',
    spinner: 'border-indigo-500',
    button: 'text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-100',
    cardBorder: 'border-indigo-100 dark:border-indigo-600',
    cardText: 'text-indigo-900 dark:text-indigo-50',
  },
  gray: {
    border: 'border-gray-200 dark:border-gray-600',
    bg: 'bg-gray-50 dark:bg-gray-900/70',
    text: 'text-gray-800 dark:text-gray-100',
    textSecondary: 'text-gray-700 dark:text-gray-200',
    spinner: 'border-gray-500',
    button: 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100',
    cardBorder: 'border-gray-100 dark:border-gray-600',
    cardText: 'text-gray-900 dark:text-gray-50',
  },
  amber: {
    border: 'border-amber-200 dark:border-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-900/70',
    text: 'text-amber-800 dark:text-amber-100',
    textSecondary: 'text-amber-700 dark:text-amber-200',
    spinner: 'border-amber-500',
    button: 'text-amber-600 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-100',
    cardBorder: 'border-amber-100 dark:border-amber-600',
    cardText: 'text-amber-900 dark:text-amber-50',
  },
  teal: {
    border: 'border-teal-200 dark:border-teal-600',
    bg: 'bg-teal-50 dark:bg-teal-900/70',
    text: 'text-teal-800 dark:text-teal-100',
    textSecondary: 'text-teal-700 dark:text-teal-200',
    spinner: 'border-teal-500',
    button: 'text-teal-600 hover:text-teal-800 dark:text-teal-300 dark:hover:text-teal-100',
    cardBorder: 'border-teal-100 dark:border-teal-600',
    cardText: 'text-teal-900 dark:text-teal-50',
  },
  violet: {
    border: 'border-violet-200 dark:border-violet-600',
    bg: 'bg-violet-50 dark:bg-violet-900/70',
    text: 'text-violet-800 dark:text-violet-100',
    textSecondary: 'text-violet-700 dark:text-violet-200',
    spinner: 'border-violet-500',
    button: 'text-violet-600 hover:text-violet-800 dark:text-violet-300 dark:hover:text-violet-100',
    cardBorder: 'border-violet-100 dark:border-violet-600',
    cardText: 'text-violet-900 dark:text-violet-50',
  },
  pink: {
    border: 'border-pink-200 dark:border-pink-600',
    bg: 'bg-pink-50 dark:bg-pink-900/70',
    text: 'text-pink-800 dark:text-pink-100',
    textSecondary: 'text-pink-700 dark:text-pink-200',
    spinner: 'border-pink-500',
    button: 'text-pink-600 hover:text-pink-800 dark:text-pink-300 dark:hover:text-pink-100',
    cardBorder: 'border-pink-100 dark:border-pink-600',
    cardText: 'text-pink-900 dark:text-pink-50',
  },
  sky: {
    border: 'border-sky-200 dark:border-sky-600',
    bg: 'bg-sky-50 dark:bg-sky-900/70',
    text: 'text-sky-800 dark:text-sky-100',
    textSecondary: 'text-sky-700 dark:text-sky-200',
    spinner: 'border-sky-500',
    button: 'text-sky-600 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-100',
    cardBorder: 'border-sky-100 dark:border-sky-600',
    cardText: 'text-sky-900 dark:text-sky-50',
  },
};

// Generic tool UI component
const GenericToolUIComponent: FC<GenericToolUIProps> = ({ args, status, result, toolName }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = getToolConfig(toolName);
  const colors = colorClasses[config.color];

  // Get the actual search query from args for search tools
  const searchQuery = args.query || args.input || '';

  if (status.type === 'running') {
    return (
      <div className={`my-2 rounded-lg border p-3 ${colors.border} ${colors.bg}`}>
        <div className="flex items-center gap-2">
          <div className={`h-4 w-4 animate-spin rounded-full border-2 border-t-transparent ${colors.spinner}`}></div>
          <span className="text-base mr-2">{config.emoji}</span>
          <span className={`font-medium text-sm ${colors.text}`}>
            {config.displayName}
          </span>
          <span className={`text-xs ${colors.textSecondary}`}>
            {config.description}
          </span>
        </div>
      </div>
    );
  }

  if (status.type === 'incomplete' && status.reason === 'error') {
    return (
      <div className="my-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-600 dark:bg-red-900/70">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">!</span>
          </div>
          <span className="text-base mr-2">{config.emoji}</span>
          <span className="font-medium text-sm text-red-800 dark:text-red-100">
            {config.displayName} Failed
          </span>
        </div>
      </div>
    );
  }

  if (status.type === 'complete' && result) {
    // Format the result for display - handle both string results and object results
    let resultContent = '';
    
    if (typeof result === 'string') {
      resultContent = result;
    } else if (result && typeof result === 'object') {
      // For search tools, extract the summary or format the results
      if (result.summary) {
        resultContent = result.summary;
      } else if (result.documents) {
        resultContent = `Found ${result.documents.length} documents:\n\n${result.documents.map((doc: any) => `**${doc.title}**\n${doc.content}`).join('\n\n')}`;
      } else {
        // For other tools, just stringify the result
        resultContent = JSON.stringify(result, null, 2);
      }
    }

    // Truncate very long content for the preview
    const previewText = resultContent.length > 200 ? `${resultContent.substring(0, 200)}...` : resultContent;

    return (
      <div className={`my-2 rounded-lg border ${colors.border} ${colors.bg}`}>
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-base">{config.emoji}</span>
              <span className={`font-medium text-sm ${colors.text}`}>
                {config.displayName}
              </span>
              <span className={`text-xs ${colors.textSecondary}`}>
                Completed
              </span>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1 rounded transition-colors text-xs ${colors.button} hover:bg-current hover:bg-opacity-20`}
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          </div>

          {isExpanded && (
            <div className={`mt-3 pt-3 border-t ${colors.border}`}>
              {/* Show query for search tools */}
              {searchQuery && (
                <div className="mb-3">
                  <div className={`text-xs font-medium mb-1 ${colors.textSecondary}`}>
                    Query:
                  </div>
                  <div className={`text-xs ${colors.text} bg-white dark:bg-gray-700 p-2 rounded border ${colors.cardBorder}`}>
                    &ldquo;{searchQuery}&rdquo;
                  </div>
                </div>
              )}
              
              {/* Show result content as markdown */}
              {resultContent && (
                <div className="mb-2">
                  <div className={`text-xs font-medium mb-1 ${colors.textSecondary}`}>
                    Result:
                  </div>
                  <div className={`${colors.text} bg-white dark:bg-gray-700 p-3 rounded border ${colors.cardBorder}`}>
                    <MarkdownText content={resultContent} className="text-xs" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// Create specific tool UI components using the generic component
export const SearchWikiToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'search_wiki',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="search_wiki" />
  ),
});

export const SearchGuidelinesToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'search_guidelines',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="search_guidelines" />
  ),
});

export const SearchUserDocsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'search_user_docs',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="search_user_docs" />
  ),
});

export const ReadBusinessRequirementsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'read_business_requirements',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="read_business_requirements" />
  ),
});

export const ReadGeneratedContentToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'read_generated_content',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="read_generated_content" />
  ),
});

export const ReadProjectContextToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'read_project_context',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="read_project_context" />
  ),
});

export const SearchBrownfieldDocsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'search_brownfield_docs',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="search_brownfield_docs" />
  ),
});

export const LookupApplicationIdsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'lookup_application_ids',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="lookup_application_ids" />
  ),
});

export const ReadProjectADRsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'read_project_adrs',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="read_project_adrs" />
  ),
});

export const ReadProjectInstructionsToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'read_project_instructions',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="read_project_instructions" />
  ),
});

export const FetchWebpageToolUI = makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
  toolName: 'fetch_webpage',
  render: ({ args, status, result }) => (
    <GenericToolUIComponent args={args} status={status} result={result} toolName="fetch_webpage" />
  ),
});

/**
 * Generic fallback component for any tool that doesn't have a specific UI defined.
 * This ensures new tools added to the backend will automatically display in the chat
 * with a generic placeholder until a custom UI component is created.
 * 
 * Note: This component won't automatically register itself - you still need to know
 * the tool name to create a specific makeAssistantToolUI wrapper for it.
 */
export const createToolUI = (toolName: string) => 
  makeAssistantToolUI<GenericToolArgs, GenericToolResult>({
    toolName,
    render: ({ args, status, result }) => (
      <GenericToolUIComponent args={args} status={status} result={result} toolName={toolName} />
    ),
  });

export default GenericToolUIComponent;
